import { useEffect, useState } from 'react';
import styles from './InviteToViewingModalNew.module.css';
import Modal from '../../../components/organisms/modal/Modal';
import ColorEnum from '../../../enums/colorEnum';
import InviteToViewingModalEventManager, { InviteToViewingModalCustomEventType, InviteToViewingModalEventData } from '../modal/InviteToViewingModalEventManager';
import { useTranslation } from 'react-i18next';
import Text from '../../atoms/typography/Text';
import { FontSizeEnum, FontWeightEnum, ColorEnum as ColorEnumNew } from '../../enums';
import { SelectNew } from '../select-new';
import Form from '../../molecules/form/Form';
import DateInput from '../../../components/atoms/dateInput/DateInput';
import { CheckboxInput } from '../../molecules/checkbox-input';
import { InputField } from '../input-field';
import HighlightBlock from '../../molecules/highlightBlock/HighlightBlock';
import { TextArea } from '../../atoms/text-area';
import FormError from '../../molecules/form/FormError';
import ButtonNew from '../../molecules/button/Button';
import MailSendLineIcon from '../../atoms/icons/MailSendLineIcon';
import { HomeseekerSelector } from '../homeseeker-selector';
import { Option } from '../select-new/SelectNew';
import TimeInput from '../../../components/atoms/timeInput/TimeInput';

const inviteToViewingModal = InviteToViewingModalEventManager.create<InviteToViewingModalEventData>('invite-to-viewing-modal');

type InviteToViewingModalComponent = () => JSX.Element;

const InviteToViewingModalNew = () => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [extraCandidates, setExtraCandidates] = useState<boolean>(false);
    const [addDateTime, setAddDateTime] = useState<boolean>(false);
    const [transactionalListingId, setTransactionalListingId] = useState<string>('');
    const [street, setStreet] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [candidateInfo, setCandidateInfo] = useState<Option>(null);
    const [formData, setFormData] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const handleModalChange = (e: InviteToViewingModalCustomEventType) => {
        setIsOpen(e.detail.open);
        setStreet(e.detail.street);
        setCandidateInfo({
            value: e.detail.homeseekerApplicationId,
            label: e.detail.fullName
        });
        setCity(e.detail.city);
        setTransactionalListingId(e.detail.transactionalListingId);
        console.log(candidateInfo);
    }

    useEffect(() => {
        inviteToViewingModal.addListener(handleModalChange);

        return () => {
            inviteToViewingModal.removeListener(handleModalChange);
        }
    }, []);

    const handleFormChange = (data, isValid, errors) => {
        setFormData(data);
        setIsFormValid(isValid);
        setFormErrors(errors);
    }

    const handleSubmit = async () => {
        console.log(formData['homeseekerSelector']?.value)
    }

    const handleCheckboxClick = () => {
        setExtraCandidates(!extraCandidates);
    }

    const handleAddDateTimeClick = () => {
        setAddDateTime(!addDateTime);
    }

    return (
        <Modal
            icon='ri-calendar-line'
            iconColor={ColorEnum.Primary600}
            iconBackgroundColor={ColorEnum.Primary100}
            flowyColor={ColorEnum.Primary100}
            manager={inviteToViewingModal}
            isOpen={isOpen}
        >
            <Form
                className={styles.viewingModal}
                onSubmit={handleSubmit}
                onChange={handleFormChange}
            >
                <Text
                    tag='p'
                    className={styles.heading}
                    weight={FontWeightEnum.Bold}
                    size={FontSizeEnum.Lg}
                >
                    {t('bulk.dropdown.invite.for.viewing')}
                </Text>
                <div>
                    <HomeseekerSelector
                        transactionalListingId={transactionalListingId}
                        // preselectedHomeseekers={[candidateInfo]}
                    />
                    <FormError errors={formErrors['selectHomeseekers']}/>
                </div>
                <div className={styles.container}>
                    <Text
                        weight={FontWeightEnum.Semibold}
                    >
                        {t('candidate.action.invite.date')}
                    </Text>
                    <div className={styles.dateSelector}>
                        <DateInput name='date' required/>
                        <FormError errors={formErrors['date']}/>
                        <TimeInput name='startTime'/>
                        {t('candidate.action.invite.date.to')}
                        <TimeInput name='endTime'/>
                    </div>
                </div>
                <div className={styles.container}>
                    <Text
                        weight={FontWeightEnum.Semibold}
                    >
                        {t('candidate.action.invite.viewing.pickableTimeslotsLabel')}
                    </Text>
                    <SelectNew
                        name="splitTimeslot"
                        placeholder={t('viewing.page.choose.timeslot.no.split')}
                        selected={formData['splitTimeslot']?.value}
                        options={[-1, 5, 10, 15, 20, 30, 60].map((m) => {
                            return {
                                label: (m > 0) ? t('candidate.action.invite.viewing.minutes', { minutes: m }) : t('viewing.page.choose.timeslot.no.split'),
                                value: m ? `${m}` : null
                            }
                        })}
                    />
                </div>
                <div className={styles.container}>
                    <CheckboxInput
                        id='others'
                        name='others'
                        label={t('candidate.action.invite.viewing.maxNumberOfCandidatesForViewing')}
                        onClick={handleCheckboxClick}
                        checked={extraCandidates}
                    />
                    <InputField 
                        value={formData['amountOfcandidates']?.value ?? 0}
                        disabled={!extraCandidates}
                        name='amountOfcandidates'
                        type='number'
                    />
                </div>
                <Text>
                    {t('candidate.action.invite.label')}
                </Text>
                <div>
                    <Text
                        weight={FontWeightEnum.Semibold}
                    >
                        {t('candidate.action.invite.subject')}
                    </Text>
                    <InputField
                        name='subject'
                        placeholder={t('candidate.action.invite.viewing', {
                            address: street,
                            city
                        })}
                        value={formData['subject']?.value ?? t('candidate.action.invite.viewing', {
                            address: street,
                            city
                        })}
                    />
                </div>
                <CheckboxInput
                    id='addDateTime'
                    name='addDateTime'
                    onClick={handleAddDateTimeClick}
                    label={
                        <label htmlFor='addDateTime' className={styles.checkboxLabel}>
                            <div className={styles.container}>
                                <Text
                                    weight={FontWeightEnum.Regular}
                                >
                                    {t('candidate.action.invite.date.includeInEmail')}
                                </Text>
                                <Text
                                    color={ColorEnumNew.Gray500}
                                    size={FontSizeEnum.Sm}
                                >
                                    {t('candidate.action.invite.date.includeInEmail.transText')}
                                </Text>
                            </div>
                            <HighlightBlock
                                type='none'
                            >
                                <Text
                                    tag='p'
                                    size={FontSizeEnum.Sm}
                                    color={ColorEnumNew.Gray500}
                                >
                                    {t('candidate.action.invite.viewing.messageBodyWithTime')}
                                </Text>
                            </HighlightBlock>
                        </label>
                    }
                    checked={addDateTime}
                />
                <div className={styles.container}>
                    <Text weight={FontWeightEnum.Regular}>
                        {t('candidate.action.invite.body')}
                    </Text>
                    <TextArea
                        id='message'
                        name='message'
                        value={formData['message']?.value}
                    />
                </div>
                <div className={styles.buttonsContainer}>
                    <ButtonNew
                        variant='secondary-gray'
                        onClick={() => { inviteToViewingModal.close() }}
                    >
                        {t('actions.cancel')}
                    </ButtonNew>
                    <ButtonNew
                        type='submit'
                        disabled={!isFormValid}
                        iconBefore={<MailSendLineIcon/>}
                    >
                        {t('viewing.invitation.send')}
                    </ButtonNew>
                </div>
            </Form>
        </Modal>
    )
}

Object.assign(InviteToViewingModalNew, inviteToViewingModal);
export default (InviteToViewingModalNew as InviteToViewingModalComponent & InviteToViewingModalEventManager<InviteToViewingModalEventData>);
